import React, { useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  FormGroup, FormControlLabel, Checkbox, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useNavigate } from 'react-router-dom';
import { exportService } from '../services/api';

const STOCK_WAREHOUSES = [
  { key: 'raw', label: 'Сировина' },
  { key: 'dyes', label: 'Барвники' },
  { key: 'packaging', label: 'Упаковка' },
  { key: 'technical', label: 'Техматеріали' },
  { key: 'consumable', label: 'Розхідні матеріали' },
  { key: 'product', label: 'Вироби / напівфабрикати' },
];

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function normalizeDefaultWarehouses(stockKind, defaultWarehouses) {
  if (Array.isArray(defaultWarehouses) && defaultWarehouses.length) {
    return defaultWarehouses;
  }
  if (!stockKind) return STOCK_WAREHOUSES.map((w) => w.key);
  if (stockKind === 'semi' || stockKind === 'packaging_work') return ['product'];
  if (STOCK_WAREHOUSES.some((w) => w.key === stockKind)) return [stockKind];
  return [stockKind];
}

/**
 * Inline Import/Export for catalog & stock pages.
 * Для stock_items — діалог вибору складів галочками перед завантаженням.
 */
export default function EntityImportExportButtons({
  entityKey,
  label,
  showImport = true,
  showExport = true,
  exportParams,
  size = 'small',
  /** Поточна вкладка складу (raw / dyes / packaging / …) — попередньо обрана */
  stockKind,
  defaultWarehouses,
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const initialWarehouses = useMemo(
    () => normalizeDefaultWarehouses(stockKind, defaultWarehouses),
    [stockKind, defaultWarehouses],
  );
  const [selectedWarehouses, setSelectedWarehouses] = useState(initialWarehouses);

  const isStockItems = entityKey === 'stock_items';

  const openExport = () => {
    if (!entityKey || busy) return;
    if (isStockItems) {
      setSelectedWarehouses(normalizeDefaultWarehouses(stockKind, defaultWarehouses));
      setDialogOpen(true);
      return;
    }
    doExport({});
  };

  const toggleWarehouse = (key) => {
    setSelectedWarehouses((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const doExport = async (extraParams) => {
    if (!entityKey || busy) return;
    setBusy(true);
    try {
      const res = await exportService.runXlsx(entityKey, {
        ...(exportParams || {}),
        ...(extraParams || {}),
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const name = `${label || entityKey}_${todayIso()}.xlsx`;
      downloadBlob(blob, name);
      setDialogOpen(false);
    } catch (e) {
      console.error(e);
      let msg = e.message || 'Помилка експорту';
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || parsed.error || msg;
        } else {
          msg = e.response?.data?.detail || msg;
        }
      } catch (_) {
        /* keep msg */
      }
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      {showExport ? (
        <Button
          variant="outlined"
          size={size}
          startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={openExport}
          disabled={busy || !entityKey}
        >
          Експорт
        </Button>
      ) : null}
      {showImport ? (
        <Button
          variant="outlined"
          size={size}
          startIcon={<FileUploadIcon />}
          onClick={() => navigate(`/import?entity=${encodeURIComponent(entityKey)}`)}
          disabled={!entityKey}
        >
          Імпорт
        </Button>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Експорт залишків — оберіть склади</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Кожен обраний склад буде окремим аркушем у Excel.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelectedWarehouses(STOCK_WAREHOUSES.map((w) => w.key))}>
              Усі
            </Button>
            <Button size="small" onClick={() => setSelectedWarehouses([])}>
              Зняти
            </Button>
          </Box>
          <FormGroup>
            {STOCK_WAREHOUSES.map((w) => (
              <FormControlLabel
                key={w.key}
                control={
                  <Checkbox
                    checked={selectedWarehouses.includes(w.key)}
                    onChange={() => toggleWarehouse(w.key)}
                    size="small"
                  />
                }
                label={w.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>Скасувати</Button>
          <Button
            variant="contained"
            disabled={busy || selectedWarehouses.length === 0}
            onClick={() => doExport({ warehouses: selectedWarehouses.join(',') })}
            startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          >
            Експортувати ({selectedWarehouses.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
